import { FC, useEffect, useState } from 'react'
import CN from 'classnames'
import { useGetSsccXmlAllList } from 'framework/api/methods/ssccXml/useGetSsccXmlAllList'

import { fileColumns, SsccXmlFileRow } from './SsccXmlRetriggeredTab'
import { SsccXmlColumn, SsccXmlTable } from './SsccXmlTable'

export interface SsccXmlAllTabProps {
  search: string
  /** yyyy-MM-dd; only applied when the range is valid. */
  from: string
  to: string
  rangeIsValid: boolean
  refreshKey?: number
}

const sourceColumn: SsccXmlColumn<SsccXmlFileRow> = {
  key: 'source',
  header: 'Source',
  width: '190px',
  render: (row) => {
    const isSystem = row.source !== 'Manually Generated'
    return (
      <span
        className={CN(
          'inline-flex px-[10px] py-[3px] rounded-full text-xs font-extrabold',
          {
            'bg-[#F0F0F2] text-[#52525B]': isSystem,
            'bg-[#FDF1E3] text-[#A8631C]': !isSystem,
          },
        )}>
        {isSystem ? 'System Generated' : 'Manually Generated'}
      </span>
    )
  },
}

/** BDL20-4003: All SSCC XML — System and Manually Generated, newest file first, read-only. */
export const SsccXmlAllTab: FC<SsccXmlAllTabProps> = ({
  search,
  from,
  to,
  rangeIsValid,
  refreshKey,
}: SsccXmlAllTabProps) => {
  const [pageSize, setPageSize] = useState(10)
  const [pageNumber, setPageNumber] = useState(1)
  const [applied, setApplied] = useState({ from: '', to: '' })

  // An invalid range (From later than To) is not applied; the last valid one stays.
  useEffect(() => {
    if (rangeIsValid) setApplied({ from, to })
  }, [from, to, rangeIsValid])

  useEffect(() => setPageNumber(1), [search, pageSize, applied])

  const { data, isLoading, refetch } = useGetSsccXmlAllList({
    search,
    from: applied.from,
    to: applied.to,
    take: pageSize,
    skip: (pageNumber - 1) * pageSize,
  })

  useEffect(() => {
    if (refreshKey) refetch()
  }, [refreshKey])

  return (
    <SsccXmlTable
      columns={[...fileColumns, sourceColumn]}
      rows={data?.items ?? []}
      rowKey={(row) => row.id}
      totalCount={data?.totalCount ?? 0}
      pageSize={pageSize}
      pageNumber={pageNumber}
      isLoading={isLoading}
      onPageChange={setPageNumber}
      onPageSizeChange={setPageSize}
    />
  )
}

export default SsccXmlAllTab
